import { Box, Theme, useTheme } from "@mui/material";
import { Map2dViewer } from "../SeatMap/Map2dViewer";
import { useDispatch } from "react-redux";
import React from "react";
import { seatMapState } from "@/global/store/data/seatMap";
import { SectionDetailsCard } from "./SectionDetailsCard";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { SectionDetailsBottomBar } from "./SectionDetailsBottomBar";
import { useMapViewerTooltip } from "./useMapViewerTooltip";
import { SeatMap2dActionItems } from "./SeatMap2dActionItems";
import { useSelectActiveEvent } from "../PurchaseSeats/ChooseSeats/useSelectActiveEvent";
import { useSeatMap2d } from "./useSeatMap2d";
import { toggleViewerNodeSelection } from "./utils.seatMap";
import type { SeatMap } from "./utils.seatMap";

export function SeatMap2d(props: { mapId: SeatMap }) {
  const { sm: smDown } = useCssMediaQuery("down");
  const dispatch = useDispatch();
  const theme = useTheme();
  const styles = useStyles(theme);
  const { venueId, mapId } = props.mapId;
  const { activeEvent } = useSelectActiveEvent();
  const {
    viewer,
    mapSections,
    setViewerInstance,
    setViewerAvailability,
    setSelectedSection,
    resetSelectedSection,
  } = useSeatMap2d();

  const {
    setIsMouseOverTooltip,
    tooltipRef,
    setTooltipNode,
    isTooltipOpen,
    tooltipNode,
    setIsTooltipOpen,
  } = useMapViewerTooltip(viewer);

  //  mobile bottomshit
  const [bottomBarOpen, setBottomBarOpen] = React.useState<boolean>(false);

  const handleClickView3d = (node: any) => {
    if (node && node.state == "available") {
      dispatch(seatMapState.actions.set({ viewId3d: node.id }));
    }
  };

  const handleClickShowSeats = (node: any) => {
    setSelectedSection(node);
    setIsTooltipOpen(false);
  };

  const handleTapSection = (obj: any) => {
    const viewer = obj.instance;
    const [node] = obj.nodes;

    if (node && node.state == "available") {
      toggleViewerNodeSelection(viewer, node);
      setTooltipNode(node);
      setBottomBarOpen(true);
    }
  };

  React.useEffect(() => {
    setViewerAvailability();
  }, [mapSections, viewer]);

  React.useEffect(() => {
    dispatch(seatMapState.actions.set({ viewId3d: "" }));
  }, [activeEvent]);

  return (
    <>
      {/* Tooltip on desktop and tablet */}
      <Box ref={tooltipRef} sx={styles.tooltip}>
        {!smDown && isTooltipOpen ? (
          <SectionDetailsCard
            sectionName={tooltipNode?.id}
            onClickView3d={() => handleClickView3d(tooltipNode)}
            onClickShowSeats={() => handleClickShowSeats(tooltipNode)}
            onMouseEnter={() => setIsMouseOverTooltip(true)}
            onMouseLeave={() => setIsMouseOverTooltip(false)}
          />
        ) : null}
      </Box>

      {smDown ? (
        <SectionDetailsBottomBar
          isOpen={bottomBarOpen}
          onClickView3d={() => {
            handleClickView3d(tooltipNode);
            setBottomBarOpen(false);
            setTooltipNode(null);
          }}
          onClose={() => {
            setBottomBarOpen(false);
            setTooltipNode(null);
            toggleViewerNodeSelection(viewer);
          }}
          onClickShowSeats={() => {
            handleClickShowSeats(tooltipNode);
            setBottomBarOpen(false);
            setTooltipNode(null);
          }}
          sectionName={tooltipNode?.id}
        />
      ) : null}

      <Box
        position={"relative"}
        bgcolor={theme.palette.background.paper}
        border={`1px solid ${theme.palette.divider}`}
      >
        {/* Action items */}
        <SeatMap2dActionItems handleReset={resetSelectedSection} />

        <Map2dViewer
          venueId={venueId}
          mapId={mapId}
          onClick={(obj) => smDown && handleTapSection(obj)}
          onLoad={(viewer) => setViewerInstance(viewer)}
        />
      </Box>
    </>
  );
}

function useStyles(theme: Theme) {
  return {
    tooltip: {
      position: "absolute",
      zIndex: 2,
    },
  };
}
